import { useCallback, useEffect, useMemo, useState } from "react";
import { downloadInvoice, fetchJobInvoicesContent } from "../service";
import DivSpinner from "../../../components/divSpinner";
import { formatMoney } from "../../../utils";
import BtnSpinner from "../../../components/btnSpinner";
import { theme } from "../../../theme";

const INVOICE_TYPE = "INSTAFIX_TO_FTM_PAYMENT_INVOICE";

const InstafixToFtm = ({ job_id }) => {
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState(null);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const getInvoicesContent = useCallback(async () => {
        if (!job_id) return;

        setLoading(true);
        try {
            const response = await fetchJobInvoicesContent(
                job_id,
                INVOICE_TYPE
            );
            setContent(response?.data || null);
        } catch (error) {
            console.error("Error fetching invoice content:", error);
        } finally {
            setLoading(false);
        }
    }, [job_id]);

    useEffect(() => {
        getInvoicesContent();
    }, [getInvoicesContent]);

    const handleDownload = useCallback(async () => {
        if (!job_id) return;

        setDownloadBtnLoading(true);
        try {
            await downloadInvoice(job_id, INVOICE_TYPE);
        } catch (error) {
            console.error("Error downloading invoice:", error);
        } finally {
            setDownloadBtnLoading(false);
        }
    }, [job_id]);

    // 🔹 Memoized destructuring (prevents rework on re-render)
    const invoice = useMemo(() => {
        if (!content) return {};
        return content;
    }, [content]);

    if (loading) {
        return (
            <div style={{ height: `${window.innerHeight - 387}px` }}>
                <DivSpinner />
            </div>
        );
    }

    if (!content) return null;

    const {
        company,
        customer,
        skill,
        job_address,
        job_id: invoiceNo,
        invoice_date,
        job_createdAt,
        per_hour_rate,
        hourly_rates_items = [],
        payment_working_hours,
        tradesperson_fee,
        job_is_approve_for_another_date,
        approve_for_another_date_additional_charge,
        tradesperson_fee_company_commission,
        ftm_requests = [],
        tradesperson_fee_balance,
        total_materials_cost,
        materials_company_commission,
        ftm_total_materials_balance,
        ftm_total_with_discount,
        ftm_payment,
        job_discount,
        total_company_commission,
        vat
    } = invoice;

    return (
        <div className="container-fluid my-3 py-3">
            <div className="row">
                <div className="col-md-10 col-lg-8 col-sm-10 mx-auto">
                    <form className="" action="index.html" method="post">
                        <div className="card my-sm-2">
                            <div className="card-header text-center">
                                <div className="row justify-content-between">
                                    <div className="col-md-4 text-start">
                                        <img
                                            className="mb-2 w-50 p-2"
                                            src={`/assets/img/instafix-logo-1.png`}
                                            alt="Logo"
                                        />
                                        <h6 className="mb-0">
                                            {company?.company_address}
                                        </h6>
                                        <p className="d-block text-secondary mb-0">
                                            Email: {company?.company_email}
                                        </p>
                                        <p className="d-block text-secondary">
                                            Phone: {company?.company_contact_number}
                                        </p>
                                    </div>
                                    <div className="col-lg-3 col-md-7 text-md-end text-start mt-5">
                                        <h6 className="d-block mt-2 mb-0">
                                            {customer?.full_name}
                                        </h6>
                                        <p className="text-secondary mb-0">
                                            Email: {customer?.email}
                                        </p>
                                        <p className="text-secondary">
                                            Phone: {customer?.contact_number}
                                        </p>
                                    </div>
                                </div>
                                <br/>
                                <div className="row justify-content-md-between">
                                    <div className="col-md-4 mt-auto">
                                        <h6 className="mb-0 text-start text-secondary font-weight-normal">
                                            Invoice no
                                        </h6>
                                        <h5 className="text-start mb-0">
                                            #{invoiceNo}
                                        </h5>
                                    </div>
                                    <div className="col-lg-5 col-md-7 mt-auto">
                                        <div className="row mt-md-5 mt-4 text-md-end text-start">
                                            <div className="col-md-6">
                                                <h6 className="text-secondary font-weight-normal mb-0">Invoice date:</h6>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="text-dark mb-0">{invoice_date}</h6>
                                            </div>
                                        </div>
                                        <div className="row text-md-end text-start">
                                            <div className="col-md-6">
                                                <h6 className="text-secondary font-weight-normal mb-0">Due date:</h6>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="text-dark mb-0">{job_createdAt}</h6>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <br />

                                <div className="row justify-content-md-between">
                                    <div className="col-md-4 mt-auto">
                                        <h6 className="mb-0 text-start text-secondary font-weight-normal">
                                            Job details
                                        </h6>
                                        <h6 className="text-start mb-0">
                                            {skill?.name} - {skill?.sub_skill?.name}
                                        </h6>
                                        <h6 className="text-start mb-0">
                                            {job_address}
                                        </h6>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="table-responsive">
                                            <table className="table text-right">
                                                <thead>
                                                    <tr>
                                                        <th scope="col" className="pe-2 text-start ps-2">
                                                            Description
                                                        </th>
                                                        <th scope="col" className="pe-2 text-end">
                                                            Sub-Total
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {hourly_rates_items.map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="pe-2 text-start ps-2">
                                                                <h6 className="text-dark mb-0">{item.title}</h6>
                                                                <p className="text-secondary mb-0">{item.description}</p>
                                                            </td>
                                                            <td className="ps-4 text-end pe-2">
                                                                {formatMoney(item.amount)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th className="h5 ps-1">
                                                            Total hourly rate
                                                        </th>
                                                        <th className="text-end h5 pe-2">
                                                            {formatMoney(per_hour_rate)}
                                                        </th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="table-responsive">
                                            <table className="table text-right">
                                                <thead>
                                                    <tr>
                                                        <th scope="col" className="pe-2 text-start ps-2">Description</th>
                                                        <th scope="col" className="pe-2">Working Hours</th>
                                                        <th scope="col" className="pe-2 text-end">Sub-Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="pe-2 text-start ps-2">Tradesperson fee</td>
                                                        <td className="ps-4">{payment_working_hours}</td>
                                                        <td className="ps-4 text-end pe-2">{formatMoney(tradesperson_fee)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pe-2 text-start ps-2 text-danger">Company commission on tradesperson fee</td>
                                                        <td className="ps-4 text-danger"></td>
                                                        <td className="ps-4 text-end text-danger pe-2">-{formatMoney(tradesperson_fee_company_commission)}</td>
                                                    </tr>
                                                    {job_is_approve_for_another_date === 1 && (
                                                        <tr>
                                                            <td className="pe-2 text-start ps-2">Additional visiting charge</td>
                                                            <td className="ps-4"></td>
                                                            <td className="ps-4 text-end pe-2">{formatMoney(approve_for_another_date_additional_charge)}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th className="h5 ps-1" colSpan="2">Total</th>
                                                        <th colSpan="1" className="text-end h5 ps-4 pe-2">{formatMoney(tradesperson_fee_balance)}</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {ftm_requests.length > 0 && (
                                <>
                                    {ftm_requests.map((request, index) => (
                                        <div className="card-body" key={index}>
                                            <div className="row">
                                                <div className="col-12">
                                                    <h6 className="text-dark mb-0">Request ID: {request.ftm_request_id}</h6>
                                                    <p className="text-secondary mb-3">{request.ftm_request_description}</p>
                                                    <div className="table-responsive">
                                                        <table className="table text-right">
                                                            <thead>
                                                                <tr>
                                                                    <th scope="col" className="pe-2 text-start ps-2">Description</th>
                                                                    <th scope="col" className="pe-2">Type</th>
                                                                    <th scope="col" className="pe-2">Qty</th>
                                                                    <th scope="col" className="pe-2">Unit Price</th>
                                                                    <th scope="col" className="pe-2 text-end">Sub-Total</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {request?.request_materials?.map((item, itemIndex) => (
                                                                    <tr key={itemIndex}>
                                                                        <td className="pe-2 text-start ps-2">{item.request_material_description}</td>
                                                                        <td className="ps-4">{item.request_material_item}</td>
                                                                        <td className="ps-4">{item.request_material_qty}</td>
                                                                        <td className="ps-4">{formatMoney(item.request_material_unit_price)}</td>
                                                                        <td className="ps-4 text-end pe-2">{formatMoney(item.request_material_net_price)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr>
                                                                    <th className="h5 ps-1" colSpan="4">
                                                                        Materials cost
                                                                    </th>
                                                                    <th className="text-end h5 ps-4 pe-2">
                                                                        {formatMoney(total_materials_cost)}
                                                                    </th>
                                                                </tr>
                                                                <tr>
                                                                    <th className="h5 ps-1 text-danger" colSpan="4">
                                                                        Company commission on materials
                                                                    </th>
                                                                    <th className="text-end h5 ps-4 pe-2 text-danger">
                                                                        -{formatMoney(materials_company_commission)}
                                                                    </th>
                                                                </tr>
                                                                <tr>
                                                                    <th className="h5 ps-1" colSpan="4">
                                                                        Total materials cost
                                                                    </th>
                                                                    <th className="text-end h5 ps-4 pe-2">
                                                                        {formatMoney(ftm_total_materials_balance)}
                                                                    </th>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="table-responsive">
                                            <table className="table text-right">
                                                <tfoot>
                                                    <tr>
                                                        <th className="h5 ps-1 text-danger">Total company commission</th>
                                                        <th className="text-end h5 ps-4 text-danger pe-2">-{formatMoney(total_company_commission)}</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="table-responsive">
                                            <table className="table text-right">
                                                <tfoot>
                                                    <tr>
                                                        <th className="h5 ps-1">Subtotal</th>
                                                        <th className="text-end h5 ps-4 pe-2">{formatMoney(ftm_total_with_discount)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th className="h5 ps-1 text-danger">Discount</th>
                                                        <th className="text-end h5 ps-4 text-danger pe-2">-{formatMoney(job_discount)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th className="h5 ps-1 text-danger">VAT ({company?.company_vat}%)</th>
                                                        <th className="text-end h5 ps-4 text-danger pe-2">-{formatMoney(vat)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th className="h5 ps-1">Your payment </th>
                                                        <th className="text-end h5 ps-4 pe-2">{formatMoney(ftm_payment)}</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer">
                                <div className="row">
                                    <div className="col-lg-5 text-left"></div>
                                    <div className="col-lg-7 text-md-end mt-md-0 mt-3">
                                        <button
                                            className={`btn btn-sm btn-icon btn-3 btn-${theme.main}`}
                                            type="button"
                                            onClick={handleDownload}
                                            disabled={downloadBtnLoading}
                                        >
                                            {downloadBtnLoading ? (
                                                <BtnSpinner />
                                            ) : (
                                                <>
                                                    <span className="btn-inner--icon">
                                                        <i className="material-icons text-lg">
                                                            download
                                                        </i>
                                                    </span>
                                                    <span className="btn-inner--text">
                                                        Download Invoice
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InstafixToFtm;